import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import TablePagination from '@mui/material/TablePagination'
import Skeleton from '@mui/material/Skeleton'
import Chip from '@mui/material/Chip'
import palette from '../../../theme/palette'
import { count, inr, pct, pct2, signedInr } from './freightPnlUtils'

/**
 * Profitability table (brands, courier codes, families, zones, lanes), sorted like the mail by default.
 * columns: [{ key, label, format: 'text'|'count'|'inr'|'signedInr'|'pct'|'pct2', align }]
 * Negative rupee / percent values render in the error colour; rows under `lossThreshold` (whole-% columns) get a chip.
 */
export default function PnlTable({ rows = [], columns, loading, lossThreshold, onRowClick, emptyText = 'No data for this scope',
  defaultOrderBy = 'percentageProfitLoss', defaultOrder = 'asc', rowKey }) {
  const [orderBy, setOrderBy] = useState(defaultOrderBy)
  const [order, setOrder] = useState(defaultOrder)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const sorted = useMemo(() => {
    const arr = [...rows]
    const dir = order === 'asc' ? 1 : -1
    arr.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      // Ties break on gross P/L in the same direction (worst first when ascending, like the mail).
      if (av === bv) return dir * ((a.grossProfitLoss ?? 0) - (b.grossProfitLoss ?? 0))
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return dir * cmp
    })
    return arr
  }, [rows, orderBy, order])

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const render = (row, col) => {
    const v = row[col.key]
    const neg = typeof v === 'number' && v < 0
    const color = neg ? palette.error.dark : undefined
    switch (col.format) {
      case 'count': return <span>{count(v)}</span>
      case 'inr': return <span style={{ color, fontWeight: neg ? 600 : 400 }}>{inr(v)}</span>
      case 'signedInr': return <span style={{ color: neg ? palette.error.dark : v > 0 ? palette.success.dark : undefined }}>{signedInr(v)}</span>
      case 'pct2': return <span style={{ color, fontWeight: neg ? 600 : 400 }}>{pct2(v)}</span>
      case 'pct': return (
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <span style={{ color, fontWeight: neg ? 600 : 400 }}>{pct(v)}</span>
          {lossThreshold !== undefined && typeof v === 'number' && v < lossThreshold && (
            <Chip size='small' label={v < 0 ? 'LOSS' : `< ${lossThreshold}%`} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: v < 0 ? palette.error.lighter : palette.warning.lighter, color: v < 0 ? palette.error.dark : palette.warning.dark }} />
          )}
        </Box>
      )
      default: return <span>{v ?? '—'}</span>
    }
  }

  if (loading) {
    return <Box sx={{ p: 2 }}>{[...Array(6)].map((_, i) => <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />)}</Box>
  }

  return (
    <>
      <TableContainer>
        <Table size='small' sx={{ '& td, & th': { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.key} align={col.align || (col.format === 'text' ? 'left' : 'right')} sx={{ bgcolor: palette.grey[200], fontWeight: 700, color: 'text.secondary' }}>
                  <TableSortLabel
                    active={orderBy === col.key}
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={() => { setOrder(orderBy === col.key && order === 'asc' ? 'desc' : 'asc'); setOrderBy(col.key); setPage(0) }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row, i) => (
              <TableRow key={(rowKey && rowKey(row)) || row.merchantOid || row.courierOid || i} hover onClick={onRowClick ? () => onRowClick(row) : undefined} sx={{ cursor: onRowClick ? 'pointer' : 'default' }}>
                {columns.map(col => (
                  <TableCell key={col.key} align={col.align || (col.format === 'text' ? 'left' : 'right')}>{render(row, col)}</TableCell>
                ))}
              </TableRow>
            ))}
            {!paged.length && (
              <TableRow><TableCell colSpan={columns.length} align='center' sx={{ py: 4, color: 'text.disabled' }}>{emptyText}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > rowsPerPage && (
        <TablePagination
          component='div' count={rows.length} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      )}
    </>
  )
}
